import { useEffect, useState } from 'react';
import { Menu, Moon, Sun, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useLogoutMutation } from '@/hooks/useAuth';

interface HeaderProps {
  onToggleSidebar: () => void;
}

type Theme = 'light' | 'dark';

function readInitialTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  const stored = window.localStorage.getItem('wartungstool.theme');
  if (stored === 'light' || stored === 'dark') return stored;
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  return prefersDark ? 'dark' : 'light';
}

export function Header({ onToggleSidebar }: HeaderProps): JSX.Element {
  const [theme, setTheme] = useState<Theme>(() => readInitialTheme());
  const logout = useLogoutMutation();

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    try {
      window.localStorage.setItem('wartungstool.theme', theme);
    } catch {
      /* ignore */
    }
  }, [theme]);

  return (
    <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-border bg-surface/80 px-4 backdrop-blur">
      <button
        type="button"
        onClick={onToggleSidebar}
        className="rounded-md p-2 text-muted hover:bg-surface-2 md:hidden"
        aria-label="Sidebar öffnen"
      >
        <Menu className="size-5" />
      </button>
      <div className="flex-1" />
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
        aria-label={theme === 'dark' ? 'Hellen Modus aktivieren' : 'Dunklen Modus aktivieren'}
        leftIcon={theme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
      >
        <span className="sr-only md:not-sr-only">
          {theme === 'dark' ? 'Hell' : 'Dunkel'}
        </span>
      </Button>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => logout.mutate()}
        loading={logout.isPending}
        leftIcon={<LogOut className="size-4" />}
      >
        Logout
      </Button>
    </header>
  );
}
